import { call, put, takeEvery } from "redux-saga/effects";
import SweetAlert from "../../../../components/sweetalert/SweetAlert";
import { downloadBase64File } from "../../../../utils/fileDownloads";

import * as types from "./configurationActionType";
import * as configurationServices from "./configurationServices";
import * as actions from "./configurationActions";

let lastTaskConfigurationFilters = {};

//--------------------- Site Coverage Encore Sagas - Start ---------------------//
// Fetch all site coverage details
function* fetchSiteCoverageDetailsSaga(action) {
  try {
    const response = yield call(
      configurationServices.getAllSiteCoverageDetails,
      action.payload,
    );
    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(actions.fetchSiteCoverageDetailsSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch site coverage details";
    console.error("Facility Coverage Details API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.fetchSiteCoverageDetailsFailure(errorMessage));
  }
}

// Fetch site coverage detail by ID
function* fetchSiteCoverageDetailByIdSaga(action) {
  try {
    const { siteCoverageID } = action.payload;
    const response = yield call(
      configurationServices.getSiteCoverageDetailByID,
      siteCoverageID,
    );
    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(actions.fetchSiteCoverageDetailByIdSuccess(response));
  } catch (error) {
    let errorMessage = "Failed to fetch facility coverage detail by ID";
    console.error("Facility Coverage Detail By ID API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.fetchSiteCoverageDetailByIdFailure(errorMessage));
  }
}

// Save site coverage detail
function* saveSiteCoverageDetailSaga(action) {
  try {
    const response = yield call(
      configurationServices.saveSiteCoverageDetail,
      action.payload,
    );
    if (response && response.success === true) {
      yield put(actions.saveSiteCoverageDetailSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully Saved Facility Coverage Detail.",
      });
      yield put(actions.fetchSiteCoverageDetailsRequest());
    } else if (response && response.success === false) {
      const errorMessage =
        response.errorMessage || "Failed to save Facility Coverage Detail";
      yield put(actions.saveSiteCoverageDetailFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    } else {
      const errorMessage = "Unexpected response from server";
      yield put(actions.saveSiteCoverageDetailFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save Facility Coverage Detail";
    yield put(actions.saveSiteCoverageDetailFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Delete site coverage detail
function* deleteSiteCoverageDetailSaga(action) {
  try {
    const { SiteCoverageID, ModifiedBy } = action.payload;
    const response = yield call(
      configurationServices.deleteSiteCoverageDetail,
      { SiteCoverageID, ModifiedBy },
    );
    if (response?.success) {
      yield put(actions.deleteSiteCoverageDetailSuccess(SiteCoverageID));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully Deleted Facility Coverage Details.",
      });
      yield put(actions.fetchSiteCoverageDetailsRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete site coverage detail";
      yield put(actions.deleteSiteCoverageDetailFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete site coverage detail";
    yield put(actions.deleteSiteCoverageDetailFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

//--------------------- Site Coverage Encore Sagas - End ---------------------//

//--------------------- Day of Week Configuration Saga - Start---------------------//
function* fetchDayOfWeekConfigurationSaga() {
  try {
    const response = yield call(
      configurationServices.getAllDayOfWeekConfiguration,
    );
    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(
      actions.fetchDayOfWeekConfigurationSuccess(response?.values || response),
    );
  } catch (error) {
    let errorMessage = "Failed to fetch Day of Week configurations";
    console.error("Day of Week Configurations API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.fetchDayOfWeekConfigurationFailure(errorMessage));
  }
}
//--------------------- Day of Week Configuration Saga - End---------------------//

//Get All Task configuration
function* fetchTaskConfigurationDetailsSaga(action) {
  try {
    const filters = action?.payload || {};
    lastTaskConfigurationFilters = filters;
    // Call the API - request utility already returns response.data
    const response = yield call(
      configurationServices.getAllTaskConfigurationDetails,
      filters,
    );
    if (!response) {
      throw new Error("No response received from API");
    }
    // Dispatch success action with the data
    yield put(
      actions.fetchTaskConfigurationRequestSuccess(
        response?.values || response,
      ),
    );
  } catch (error) {
    let errorMessage = "Failed to fetch Task Configuration details";
    console.error("Task Configuration API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    // Dispatch failure action with error
    yield put(actions.fetchTaskConfigurationRequestFailure(errorMessage));
  }
}

// Block Planning Worker saga: will be fired on FETCH_BLOCK_PLANNING_REQUEST actions
function* fetchBlockPlanningSaga() {
  try {
    const response = yield call(configurationServices.getAllBlockPlanning);
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchBlockPlanningSuccess(response?.values || response));
  } catch (error) {
    let errorMessage = "Failed to fetch block planning";

    console.error("Block Planning API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchBlockPlanningFailure(errorMessage));
  }
}

// Shift Hour Configuration Saga
function* fetchShiftHourSaga() {
  try {
    const response = yield call(
      configurationServices.getShiftHourConfiguration,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchShiftHourSuccess(response?.values || response));
  } catch (error) {
    let errorMessage = "Failed to fetch shift hour configuration";

    console.error("Shift Hour Configuration API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    yield put(actions.fetchShiftHourFailure(errorMessage));
  }
}

// Coverage Gap Sagas
function* fetchCoverageGapSaga() {
  try {
    // Call the API - request utility already returns response.data
    const response = yield call(configurationServices.getCoverageGap);
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(actions.fetchCoverageGapSuccess(response?.values || response));
  } catch (error) {
    let errorMessage = "Failed to fetch coverage gaps";

    console.error("Coverage Gaps API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchCoverageGapFailure(errorMessage));
  }
}

function* fetchCoverageGapByIdSaga(action) {
  try {
    const { RVUConfigurationID } = action.payload;
    // Call the API - request utility already returns response.data
    const response = yield call(
      configurationServices.getCoverageGapByID,
      RVUConfigurationID,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(
      actions.fetchCoverageGapByIdSuccess(response?.values || response),
    );
  } catch (error) {
    let errorMessage = "Failed to fetch coverage gap detail";

    console.error("Coverage Gap Detail By ID API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchCoverageGapByIdFailure(errorMessage));
  }
}

//DELETE_COVERAGE_GAP_REQUEST action
function* deleteCoverageGapSaga(action) {
  try {
    const { RVUConfigurationID, ModifiedBy } = action.payload;
    const response = yield call(
      configurationServices.deleteCoverageGap,
      RVUConfigurationID,
      ModifiedBy,
    );
    if (response?.success) {
      yield put(actions.deleteCoverageGapSuccess(RVUConfigurationID));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Deleted Coverage Gap.",
      });
      yield put(actions.fetchCoverageGapRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to Delete Coverage Gap";
      yield put(actions.deleteCoverageGapFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to Delete Coverage Gap";
    yield put(actions.deleteCoverageGapFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* saveCoverageGapSaga(action) {
  try {
    const response = yield call(
      configurationServices.saveCoverageGap,
      action.payload,
    );
    if (response && response.success === true) {
      yield put(actions.saveCoverageGapSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Saved Coverage Gap.",
      });
      yield put(actions.fetchCoverageGapRequest());
    } else if (response && response.success === false) {
      const errorMessage =
        response.errorMessage || "Failed to save Coverage Gap";
      yield put(actions.saveCoverageGapFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    } else {
      const errorMessage = "Unexpected response from server";
      yield put(actions.saveCoverageGapFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save Coverage Gap";
    yield put(actions.saveCoverageGapFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga: will be fired on DELETE_BLOCK_PLANNING_REQUEST actions
function* deleteBlockPlanningDetailSaga(action) {
  try {
    const { BlockPlanningColorConfigurationID, ModifiedBy } = action.payload;
    const response = yield call(
      configurationServices.deleteBlockPlanning,
      BlockPlanningColorConfigurationID,
      ModifiedBy,
    );
    if (response?.success) {
      yield put(
        actions.deleteBlockPlanningSuccess(BlockPlanningColorConfigurationID),
      );
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully Deleted Block Planning Detail.",
      });
      yield put(actions.fetchBlockPlanningRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to Delete Block Planning Detail";
      yield put(actions.deleteBlockPlanningFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to Delete Block Planning Detail";
    yield put(actions.deleteBlockPlanningFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga: will be fired on FETCH_BLOCK_PLANNING_BY_ID_REQUEST actions
function* fetchBlockPlanningDetailByIdRequest(action) {
  try {
    const { blockPlanningColorConfigurationID } = action.payload;
    // Call the API - request utility already returns response.data
    const response = yield call(
      configurationServices.getBlockPlanningDetailByID,
      blockPlanningColorConfigurationID,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(
      actions.fetchBlockPlanningDetailByIdSuccess(response?.values || response),
    );
  } catch (error) {
    let errorMessage =
      "Failed to fetch Block Planning Color Configuration detail";

    console.error(
      "Block Planning Color Configuration Detail By ID API Error:",
      {
        status: error.response?.status,
        message: errorMessage,
        data: error.response?.data,
        timestamp: new Date().toISOString(),
      },
    );

    // Dispatch failure action with error
    yield put(actions.fetchBlockPlanningDetailByIdFailure(errorMessage));
  }
}
// ...existing code...

//Save task configuration
function* saveTaskConfigurationSaga(action) {
  try {
    const payload = action.payload;
    const response = yield call(
      configurationServices.saveTaskConfiguration,
      payload,
    );
    if (response?.isSuccess === false || response?.success === false) {
      const errorMessage =
        response?.message ||
        response?.errorMessage ||
        "Failed to save task configuration detail";
      const normalizedError = (errorMessage || "").toLowerCase();
      const isDuplicate =
        normalizedError.includes("already exist") ||
        normalizedError.includes("duplicate");

      if (isDuplicate) {
        yield put(
          actions.saveTaskConfigurationFailure({
            inlineError: errorMessage,
            error: errorMessage,
          }),
        );
      } else {
        yield put(actions.saveTaskConfigurationFailure(errorMessage));
        SweetAlert.error({
          title: "Error!",
          text: errorMessage,
        });
      }
    } else {
      yield put(actions.saveTaskConfigurationSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: "Task configuration saved successfully!",
      });
      yield put(
        actions.fetchTaskConfigurationRequestRequest(lastTaskConfigurationFilters),
      );
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to save task configuration detail";
    const normalizedError = (errorMessage || "").toLowerCase();
    const isDuplicate =
      normalizedError.includes("already exist") ||
      normalizedError.includes("duplicate");
    if (isDuplicate) {
      yield put(
        actions.saveTaskConfigurationFailure({
          inlineError: errorMessage,
          error: errorMessage,
        }),
      );
    } else {
      yield put(actions.saveTaskConfigurationFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  }
}

//Get Task configuration by ID
function* fetchTaskConfigurationByIdSaga(action) {
  try {
    // const taskId = action.payload;
    const taskId = action.payload.taskConfigurationID;
    const response = yield call(
      configurationServices.getTaskConfigurationById,
      taskId,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(
      actions.fetchTaskConfigurationByIdSuccess(response?.values || response),
    );
  } catch (error) {
    let errorMessage = "Failed to fetch task configuration by ID";

    console.error("Task Configuration API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchTaskConfigurationByIdFailure(errorMessage));
  }
}

//Delete Task Configuration
function* deleteTaskConfigurationSaga(action) {
  try {
    const { TaskID, ModifiedBy, taskname } = action.payload;
    const response = yield call(configurationServices.deleteTaskConfiguration, {
      TaskID,
      ModifiedBy,
      taskname,
    });
    if (response?.success) {
      yield put(actions.deleteTaskConfigurationSuccess(response));
      SweetAlert.success({
        title: "Deleted!",
        text:
          response?.resultMessage ||
          "Task configuration has been deleted successfully.",
      });
      yield put(
        actions.fetchTaskConfigurationRequestRequest(lastTaskConfigurationFilters),
      );
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete site coverage detail";
      yield put(actions.deleteTaskConfigurationFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete site coverage detail";
    yield put(actions.deleteTaskConfigurationFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

//Activate & Deactivate Task Configuration
function* activateDeactivateTaskConfigurationSaga(action) {
  try {
    const { taskConfigurationID, isActive, userId } = action.payload;
    const response = yield call(
      configurationServices.activateDeactivateTaskConfiguration,
      taskConfigurationID,
      isActive,
      userId,
    );
    yield put(
      actions.activateDeactivatefetchTaskConfigurationByIdSuccess(response),
    );
    if (response && response.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Task configuration has been ${isActive ? "activated" : "deactivated"} successfully.`,
      });
      yield put(
        actions.fetchTaskConfigurationRequestRequest(lastTaskConfigurationFilters),
      );
    } else {
      SweetAlert.error({
        title: "Error!",
        text:
          response?.errorMessage ||
          `Failed to ${isActive ? "activate" : "deactivate"} task configuration.`,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      `Failed to ${action.payload?.isActive ? "activate" : "deactivate"} task configuration.`;
    yield put(
      actions.activateDeactivatefetchTaskConfigurationByIdFailure(errorMessage),
    );
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* saveTaskBulkUpdateSaga(action) {
  try {
    const response = yield call(
      configurationServices.saveTaskBulkUpdate,
      action.payload,
    );
    if (response && response.success === true) {
      yield put(actions.taskBulkUpdateSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage || "Successfully Saved Task Configuration.",
      });
      yield put(
        actions.fetchTaskConfigurationRequestRequest(lastTaskConfigurationFilters),
      );
    } else if (response && response.success === false) {
      const errorMessage =
        response.errorMessage || "Failed to save Task Configuration";
      yield put(actions.taskBulkUpdateFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    } else {
      const errorMessage = "Unexpected response from server";
      yield put(actions.taskBulkUpdateFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save Task Configuration.";
    yield put(actions.taskBulkUpdateFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchTaskTagsSaga(action) {
  try {
    const { taskId } = action.payload;
    const response = yield call(
      configurationServices.getTaskTagsByTaskId,
      taskId,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchTaskTagsSuccess(response?.values || response));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch task tags.";
    yield put(actions.fetchTaskTagsFailure(errorMessage));
  }
}

function* saveTaskTagsSaga(action) {
  try {
    const { data, onSuccess } = action.payload;
    const response = yield call(
      configurationServices.saveTaskTags,
      data,
    );
    if (response?.success === false) {
      const errorMessage =
        response?.errorMessage || response?.message || "Failed to save task tags.";
      yield put(actions.saveTaskTagsFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
      return;
    }

    yield put(actions.saveTaskTagsSuccess(response));
    SweetAlert.success({
      title: "Success!",
      text: response?.resultMessage || "Task tags saved successfully.",
    });

    if (data?.TaskID) {
      yield put(actions.fetchTaskTagsRequest(data.TaskID));
    }

    if (typeof onSuccess === "function") {
      onSuccess(response);
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to save task tags.";
    yield put(actions.saveTaskTagsFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchProviderTaskPoolSaga(action) {
  try {
    const { taskId } = action.payload;
    const response = yield call(
      configurationServices.getProviderTaskPoolByTaskId,
      taskId,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchProviderTaskPoolSuccess(response));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch provider pool details.";
    yield put(actions.fetchProviderTaskPoolFailure(errorMessage));
  }
}

function* saveProviderTaskPoolSaga(action) {
  try {
    const { data, onSuccess } = action.payload;
    const response = yield call(configurationServices.saveProviderTaskPool, data);

    if (response?.success === false) {
      const errorMessage =
        response?.errorMessage ||
        response?.message ||
        "Failed to save provider pool.";
      yield put(actions.saveProviderTaskPoolFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
      return;
    }

    yield put(actions.saveProviderTaskPoolSuccess(response));
    SweetAlert.success({
      title: "Success!",
      text: response?.resultMessage || "Provider pool saved successfully.",
    });

    if (typeof onSuccess === "function") {
      onSuccess(response);
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to save provider pool.";
    yield put(actions.saveProviderTaskPoolFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchProviderTaskPoolProviderOptionsSaga(action) {
  try {
    const { taskId } = action.payload;
    const response = yield call(
      configurationServices.getProviderTaskPoolProviderOptions,
      taskId,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchProviderTaskPoolProviderOptionsSuccess(response));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to load provider options.";
    yield put(actions.fetchProviderTaskPoolProviderOptionsFailure(errorMessage));
  }
}

function* addProviderTaskPoolProvidersSaga(action) {
  try {
    const { data, onSuccess } = action.payload;
    const response = yield call(
      configurationServices.addProviderTaskPoolProviders,
      data,
    );

    if (response?.success === false) {
      const errorMessage =
        response?.errorMessage ||
        response?.message ||
        "Failed to add providers to pool.";
      yield put(actions.addProviderTaskPoolProvidersFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
      return;
    }

    yield put(actions.addProviderTaskPoolProvidersSuccess(response));

    if (typeof onSuccess === "function") {
      onSuccess(response);
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to add providers to pool.";
    yield put(actions.addProviderTaskPoolProvidersFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* removeProviderTaskPoolProviderSaga(action) {
  try {
    const { data, onSuccess } = action.payload;
    const response = yield call(
      configurationServices.removeProviderTaskPoolProvider,
      data,
    );

    if (response?.success === false) {
      const errorMessage =
        response?.errorMessage ||
        response?.message ||
        "Failed to remove provider from pool.";
      yield put(actions.removeProviderTaskPoolProviderFailure(errorMessage));
      const isReferenceBlock = /deletion not allowed|referenced data exists/i.test(
        errorMessage,
      );
      if (isReferenceBlock) {
        SweetAlert.warning({
          title: "Warning!",
          text: errorMessage,
        });
      } else {
        SweetAlert.error({
          title: "Error!",
          text: errorMessage,
        });
      }
      return;
    }

    yield put(actions.removeProviderTaskPoolProviderSuccess(response));

    SweetAlert.success({
      title: "Success!",
      text:
        response?.resultMessage ||
        response?.message ||
        "Provider removed from pool successfully.",
    });

    if (typeof onSuccess === "function") {
      onSuccess(response);
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to remove provider from pool.";
    yield put(actions.removeProviderTaskPoolProviderFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* exportProviderTaskPoolSaga(action) {
  try {
    const { taskId, taskName } = action.payload;
    const response = yield call(configurationServices.exportProviderTaskPool, taskId);

    if (!response) {
      throw new Error("No response received from API");
    }

    if (response?.success === false) {
      const errorMessage =
        response?.errorMessage ||
        response?.message ||
        "Failed to export provider pool.";
      yield put(actions.exportProviderTaskPoolFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
      return;
    }

    const base64Value =
      response?.values || response?.value || response?.Values || response?.Value || "";
    const safeTaskName = (taskName || "")
      .toString()
      .trim()
      .replace(/[\\/:*?"<>|]/g, "_")
      .replace(/\s+/g, " ");
    const exportFileName = safeTaskName
      ? `TaskProviderPool_${safeTaskName}.xlsx`
      : `TaskProviderPool_${taskId}.xlsx`;
    const isDownloaded = yield call(
      downloadBase64File,
      base64Value,
      "TaskProviderPool",
      exportFileName,
    );

    if (!isDownloaded) {
      throw new Error("Failed to download exported provider pool file.");
    }

    yield put(actions.exportProviderTaskPoolSuccess(response));
    SweetAlert.success({
      title: "Success!",
      text: response?.resultMessage || "Task provider pool exported successfully.",
    });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to export provider pool.";
    yield put(actions.exportProviderTaskPoolFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

//Priority SLA Mapping

function* fetchPrioritySLAMappingsSaga() {
  try {
    const response = yield call(
      configurationServices.getAllPrioritySLAMappings,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    // Extract the actual data from nested structure
    const data =
      response?.values?.listPrioritySLA || response?.listPrioritySLA || [];
    yield put(actions.fetchPrioritySLAMappingsSuccess(data));
  } catch (error) {
    let errorMessage = "Failed to fetch priority SLA mappings";

    console.error("Priority SLA Mapping API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchPrioritySLAMappingsFailure(errorMessage));
  }
}

function* deletePrioritySLAMappingSaga(action) {
  try {
    const { mappingId, modifiedBy } = action.payload;
    const response = yield call(
      configurationServices.deletePrioritySLAMapping,
      mappingId,
      modifiedBy,
    );
    if (response?.success) {
      yield put(
        actions.deletePrioritySLAMappingSuccess(response?.values || response),
      );
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully Deleted Priority SLA Mapping.",
      });
      yield put(actions.fetchPrioritySLAMappingsRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete priority SLA mapping.";
      yield put(actions.deletePrioritySLAMappingFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete priority SLA mapping.";
    yield put(actions.deletePrioritySLAMappingFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* savePrioritySLAMappingSaga(action) {
  try {
    const mappingData = action.payload;
    const response = yield call(
      configurationServices.savePrioritySLAMapping,
      mappingData,
    );
    if (response?.success) {
      yield put(
        actions.savePrioritySLAMappingSuccess(response?.values || response),
      );
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage || "Successfully Saved Priority SLA Mapping.",
      });
      yield put(actions.fetchPrioritySLAMappingsRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save priority SLA mapping.";
      yield put(actions.savePrioritySLAMappingFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save priority SLA mapping.";
    yield put(actions.savePrioritySLAMappingFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchPrioritySLAMappingByIdSaga(action) {
  try {
    const mappingId = action.payload;
    const response = yield call(
      configurationServices.getPrioritySLAMappingById,
      mappingId,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(
      actions.fetchPrioritySLAMappingByIdSuccess(response?.values || response),
    );
  } catch (error) {
    let errorMessage = "Failed to fetch priority SLA mapping by ID";

    console.error("Priority SLA Mapping API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchPrioritySLAMappingByIdFailure(errorMessage));
  }
}

function* dropdownPrioritySaga() {
  try {
    const response = yield call(configurationServices.getPriorityDropdown);
    if (!response) {
      throw new Error("No response received from API");
    }
    // Dispatch success action with the data
    yield put(actions.dropdownPrioritySuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch priority dropdown";

    console.error("Priority Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    // Dispatch failure action with error
    yield put(actions.dropdownPriorityFailure(errorMessage));
  }
}

function* activateDeactivatePrioritySLAMappingSaga(action) {
  try {
    const { mappingId, status, userId } = action.payload;
    const response = yield call(
      configurationServices.activateDeactivatePrioritySLAMapping,
      mappingId,
      status,
      userId,
    );
    if (response?.success) {
      yield put(
        actions.activateDeactivatePrioritySLAMappingSuccess(
          response?.values || response,
        ),
      );
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Successfully ${status ? "activated" : "deactivated"} Priority Mapping.`,
      });
      yield put(actions.fetchPrioritySLAMappingsRequest());
    } else {
      const errorMessage =
        response?.errorMessage ||
        `Failed to ${status ? "activate" : "deactivate"} Priority Mapping.`;
      yield put(actions.activateDeactivatePrioritySLAMappingFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update Priority Mapping status.";
    yield put(actions.activateDeactivatePrioritySLAMappingFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}


function* fetchPrioritySLABucketsSaga() {
  try {
    const response = yield call(
      configurationServices.getPriorityBucketsForPrioritySLA,
    );
    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(actions.fetchPrioritySLABucketsSuccess(response?.values));
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch priority buckets";

    yield put(actions.fetchPrioritySLABucketsFailure(errorMessage));
  }
}

// License Mapping Saga
function* fetchStateLicenseMappingSaga() {
  try {
    const response = yield call(configurationServices.getStateLicenseMapping);
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchStateLicenseMappingSuccess(response?.values || response),
    );
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch License Mapping.";

    yield put(actions.fetchStateLicenseMappingFailure(errorMessage));
  }
}

function* fetchStateLicenseMappingDropdownSaga() {
  try {
    const response = yield call(
      configurationServices.getStateLicenseMappingDropdown,
    );

    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchStateLicenseMappingDropdownSuccess(
        response?.values || response,
      ),
    );
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch License Mapping dropdown.";

    yield put(actions.fetchStateLicenseMappingDropdownFailure(errorMessage));
  }
}

function* saveStateLicenseMappingBulkSaga(action) {
  try {
    const response = yield call(
      configurationServices.saveStateLicenseMappingBulk,
      action.payload,
    );

    if (response?.success) {
      yield put(actions.saveStateLicenseMappingBulkSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully saved License Mapping details.",
      });

      yield put(actions.fetchStateLicenseMappingRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save License Mapping details.";
      yield put(actions.saveStateLicenseMappingBulkFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to save License Mapping details.";
    yield put(actions.saveStateLicenseMappingBulkFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Privileges Mapping Saga
function* fetchPrivilegesMappingSaga() {
  try {
    const response = yield call(configurationServices.getPrivilegesMapping);
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchPrivilegesMappingSuccess(response?.values || response));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch Privileges Mapping.";

    yield put(actions.fetchPrivilegesMappingFailure(errorMessage));
  }
}

function* fetchPrivilegesMappingDropdownSaga() {
  try {
    const response = yield call(configurationServices.getPrivilegesMappingDropdown);

    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchPrivilegesMappingDropdownSuccess(response?.values || response),
    );
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch Privileges Mapping dropdown.";

    yield put(actions.fetchPrivilegesMappingDropdownFailure(errorMessage));
  }
}

function* savePrivilegesMappingBulkSaga(action) {
  try {
    const response = yield call(
      configurationServices.savePrivilegesMappingBulk,
      action.payload,
    );

    if (response?.success) {
      yield put(actions.savePrivilegesMappingBulkSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully saved Privileges Mapping details.",
      });

      yield put(actions.fetchPrivilegesMappingRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save Privileges Mapping details.";
      yield put(actions.savePrivilegesMappingBulkFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to save Privileges Mapping details.";
    yield put(actions.savePrivilegesMappingBulkFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Reading Status Mapping Saga
function* fetchReadingStatusMappingSaga() {
  try {
    const response = yield call(configurationServices.getReadingStatusMapping);
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchReadingStatusMappingSuccess(response?.values || response));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch Reading Status Mapping.";

    yield put(actions.fetchReadingStatusMappingFailure(errorMessage));
  }
}

function* fetchReadingStatusMappingDropdownSaga() {
  try {
    const response = yield call(
      configurationServices.getReadingStatusMappingDropdown,
    );

    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchReadingStatusMappingDropdownSuccess(
        response?.values || response,
      ),
    );
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch Reading Status Mapping dropdown.";

    yield put(actions.fetchReadingStatusMappingDropdownFailure(errorMessage));
  }
}

function* saveReadingStatusMappingBulkSaga(action) {
  try {
    const response = yield call(
      configurationServices.saveReadingStatusMappingBulk,
      action.payload,
    );

    if (response?.success) {
      yield put(actions.saveReadingStatusMappingBulkSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully saved Reading Status Mapping details.",
      });

      yield put(actions.fetchReadingStatusMappingRequest());
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to save Reading Status Mapping details.";
      yield put(actions.saveReadingStatusMappingBulkFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to save Reading Status Mapping details.";
    yield put(actions.saveReadingStatusMappingBulkFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

 
function* fetchDefineMismatchSaga() {
  try {
    const response = yield call(configurationServices.getDefineMismatch);
    if (!response) {
      throw new Error("No response received from API");
    }
 
    yield put(actions.fetchDefineMismatchSuccess(response?.values || response));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch Define Mismatch details.";
 
    yield put(actions.fetchDefineMismatchFailure(errorMessage));
  }
}
 
function* fetchDefineMismatchDropdownSaga() {
  try {
    const response = yield call(configurationServices.getDefineMismatchDropdown);
    if (!response) {
      throw new Error("No response received from API");
    }
 
    yield put(
      actions.fetchDefineMismatchDropdownSuccess(response?.values || response),
    );
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch Define Mismatch dropdown.";
 
    yield put(actions.fetchDefineMismatchDropdownFailure(errorMessage));
  }
}
 
function* saveDefineMismatchSaga(action) {
  try {
    const response = yield call(configurationServices.saveDefineMismatch, action.payload);
 
    if (response?.success) {
      yield put(actions.saveDefineMismatchSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully saved Define Mismatch details.",
      });
      yield put(actions.fetchDefineMismatchRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save Define Mismatch details.";
      yield put(actions.saveDefineMismatchFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to save Define Mismatch details.";
    yield put(actions.saveDefineMismatchFailure(errorMessage));
  }
}
 
function* activateDeactivateDefineMismatchSaga(action) {
  try {
    const response = yield call(
      configurationServices.activateDeactivateDefineMismatch,
      action.payload,
    );
 
    if (response?.success) {
      yield put(actions.activateDeactivateDefineMismatchSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully updated Define Mismatch status.",
      });
      yield put(actions.fetchDefineMismatchRequest());
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to update Define Mismatch status.";
      yield put(actions.activateDeactivateDefineMismatchFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update Define Mismatch status.";
    yield put(actions.activateDeactivateDefineMismatchFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}
 
function* deleteDefineMismatchSaga(action) {
  try {
    const response = yield call(configurationServices.deleteDefineMismatch, action.payload);
 
    if (response?.success) {
      yield put(actions.deleteDefineMismatchSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully deleted Define Mismatch details.",
      });
      yield put(actions.fetchDefineMismatchRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete Define Mismatch details.";
      yield put(actions.deleteDefineMismatchFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to delete Define Mismatch details.";
    yield put(actions.deleteDefineMismatchFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

//--------------------- DLS Configuration Saga - Start---------------------//
function* fetchDLSConfigurationSaga() {
  try {
    const response = yield call(configurationServices.getAllDLSConfiguration);
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchDLSConfigurationSuccess(response?.values || response),
    );
    // Also populate sites without DLS from the same response
    yield put(
      actions.fetchSitesWithoutDLSSuccess(response?.values || response),
    );
  } catch (error) {
    let errorMessage = "Failed to fetch DLS configurations";

    console.error("DLS Configurations API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    yield put(actions.fetchDLSConfigurationFailure(errorMessage));
    yield put(actions.fetchSitesWithoutDLSFailure(errorMessage));
  }
}

function* saveDLSConfigurationSaga(action) {
  try {
    const response = yield call(
      configurationServices.saveDLSConfiguration,
      action.payload,
    );
    if (response.success) {
      yield put(actions.saveDLSConfigurationSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response.resultMessage || "DLS Configuration saved successfully.",
      });
      yield put(actions.fetchDLSConfigurationRequest());
    } else {
      const errorMessage =
        response.errorMessage || "Failed to save DLS configuration.";
      yield put(actions.saveDLSConfigurationFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save DLS configuration.";
    yield put(actions.saveDLSConfigurationFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}
//--------------------- DLS Configuration Saga - End---------------------//

function* saveBlockPlanningSaga(action) {
  try {
    const response = yield call(
      configurationServices.saveBlockPlanning,
      action.payload,
    );
    if (response && response.success === true) {
      yield put(actions.saveBlockPlanningSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully Saved Block Planning Color Configuration.",
      });
      yield put(actions.fetchBlockPlanningRequest());
    } else if (response && response.success === false) {
      const errorMessage =
        response.errorMessage ||
        "Failed to save Block Planning Color Configuration";
      yield put(actions.saveBlockPlanningFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    } else {
      const errorMessage = "Unexpected response from server";
      yield put(actions.saveBlockPlanningFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save Block Planning Color Configuration.";
    yield put(actions.saveBlockPlanningFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Simulation Cases Sagas
function* fetchSimulationDetailsSaga(action) {
  try {
    // Call the API - request utility already returns response.data
    const response = yield call(configurationServices.getAllSimulation);
    if (!response) {
      throw new Error("No response received from API");
    }
    // Dispatch success action with the data
    yield put(actions.fetchSimulationSuccess(response?.values || response));
  } catch (error) {
    let errorMessage = "Failed to fetch simulation details";
    console.error("Simulation Details API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.fetchSimulationFailure(errorMessage));
  }
}

function* saveSimulationDetailsSaga(action) {
  try {
    const response = yield call(
      configurationServices.saveSimulation,
      action.payload,
    );
    if (response && response.success === true) {
      yield put(actions.saveSimulationSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully Saved Simulation Color Configuration.",
      });
      yield put(actions.fetchSimulationRequest());
    } else if (response && response.success === false) {
      const errorMessage =
        response.errorMessage ||
        "Failed to save Simulation Color Configuration.";
      yield put(actions.saveSimulationFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    } else {
      const errorMessage = "Unexpected response from server";
      yield put(actions.saveSimulationFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save Simulation Color Configuration.";
    yield put(actions.saveSimulationFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* deleteSimulationDetailSaga(action) {
  try {
    const { SimulatorLegendId, ModifiedBy } = action.payload;
    const response = yield call(
      configurationServices.deleteSimulation,
      SimulatorLegendId,
      ModifiedBy,
    );
    if (response?.success) {
      yield put(actions.deleteSimulationSuccess(SimulatorLegendId));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Deleted Simulation.",
      });
      yield put(actions.fetchSimulationRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete simulation.";
      yield put(actions.deleteSimulationFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete simulation.";
    yield put(actions.deleteSimulationFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchSimulationDetailByIdSaga(action) {
  try {
    const { SimulatorLegendId } = action.payload;
    // Call the API - request utility already returns response.data
    const response = yield call(
      configurationServices.getSimulationByID,
      SimulatorLegendId,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(actions.fetchSimulationByIdSuccess(response?.values || response));
  } catch (error) {
    let errorMessage = "Failed to fetch simulation detail";

    console.error("Simulation Detail By ID API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchSimulationByIdFailure(errorMessage));
  }
}
//--------------------- Simulation Configuration Saga - End---------------------//

// Watcher saga: spawns new saga tasks on each request
//common configuration sagas
function* configurationSaga() {
  yield takeEvery(
    types.FETCH_SITE_COVERAGE_DETAILS_REQUEST,
    fetchSiteCoverageDetailsSaga,
  );
  yield takeEvery(
    types.FETCH_SITE_COVERAGE_DETAIL_BY_ID_REQUEST,
    fetchSiteCoverageDetailByIdSaga,
  );
  yield takeEvery(
    types.SAVE_SITE_COVERAGE_DETAIL_REQUEST,
    saveSiteCoverageDetailSaga,
  );
  yield takeEvery(
    types.DELETE_SITE_COVERAGE_DETAIL_REQUEST,
    deleteSiteCoverageDetailSaga,
  );

  // Coverage Gap watchers
  yield takeEvery(types.FETCH_COVERAGE_GAP_REQUEST, fetchCoverageGapSaga);
  yield takeEvery(
    types.FETCH_COVERAGE_GAP_BY_ID_REQUEST,
    fetchCoverageGapByIdSaga,
  );
  yield takeEvery(types.DELETE_COVERAGE_GAP_REQUEST, deleteCoverageGapSaga);
  yield takeEvery(types.SAVE_COVERAGE_GAP_REQUEST, saveCoverageGapSaga);

  // Priority SLA Mapping watchers
  yield takeEvery(
    types.FETCH_PRIORITY_SLA_MAPPINGS_REQUEST,
    fetchPrioritySLAMappingsSaga,
  );
  yield takeEvery(
    types.FETCH_PRIORITY_SLA_MAPPING_BY_ID_REQUEST,
    fetchPrioritySLAMappingByIdSaga,
  );
  yield takeEvery(
    types.SAVE_PRIORITY_SLA_MAPPING_REQUEST,
    savePrioritySLAMappingSaga,
  );
  yield takeEvery(
    types.DELETE_PRIORITY_SLA_MAPPING_REQUEST,
    deletePrioritySLAMappingSaga,
  );
  yield takeEvery(types.DROPDOWN_PRIORITY_REQUEST, dropdownPrioritySaga);
  
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_PRIORITY_SLA_MAPPING_REQUEST,
    activateDeactivatePrioritySLAMappingSaga,
  );
  yield takeEvery(
    types.FETCH_PRIORITY_SLA_BUCKETS_REQUEST,
    fetchPrioritySLABucketsSaga,
  );

  // License Mapping
  yield takeEvery(
    types.FETCH_STATE_LICENSE_MAPPING_REQUEST,
    fetchStateLicenseMappingSaga,
  );
  yield takeEvery(
    types.FETCH_STATE_LICENSE_MAPPING_DROPDOWN_REQUEST,
    fetchStateLicenseMappingDropdownSaga,
  );
  yield takeEvery(
    types.SAVE_STATE_LICENSE_MAPPING_BULK_REQUEST,
    saveStateLicenseMappingBulkSaga,
  );
  yield takeEvery(types.FETCH_DEFINE_MISMATCH_REQUEST, fetchDefineMismatchSaga);
  yield takeEvery(
    types.FETCH_DEFINE_MISMATCH_DROPDOWN_REQUEST,
    fetchDefineMismatchDropdownSaga,
  );
  yield takeEvery(types.SAVE_DEFINE_MISMATCH_REQUEST, saveDefineMismatchSaga);
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_DEFINE_MISMATCH_REQUEST,
    activateDeactivateDefineMismatchSaga,
  );
  yield takeEvery(types.DELETE_DEFINE_MISMATCH_REQUEST, deleteDefineMismatchSaga);

  // Privileges Mapping
  yield takeEvery(
    types.FETCH_PRIVILEGES_MAPPING_REQUEST,
    fetchPrivilegesMappingSaga,
  );
  yield takeEvery(
    types.FETCH_PRIVILEGES_MAPPING_DROPDOWN_REQUEST,
    fetchPrivilegesMappingDropdownSaga,
  );
  yield takeEvery(
    types.SAVE_PRIVILEGES_MAPPING_BULK_REQUEST,
    savePrivilegesMappingBulkSaga,
  );

  // Reading Status Mapping
  yield takeEvery(
    types.FETCH_READING_STATUS_MAPPING_REQUEST,
    fetchReadingStatusMappingSaga,
  );
  yield takeEvery(
    types.FETCH_READING_STATUS_MAPPING_DROPDOWN_REQUEST,
    fetchReadingStatusMappingDropdownSaga,
  );
  yield takeEvery(
    types.SAVE_READING_STATUS_MAPPING_BULK_REQUEST,
    saveReadingStatusMappingBulkSaga,
  );


  // Shift Hour Configuration watcher
  yield takeEvery(types.FETCH_SHIFT_HOUR_REQUEST, fetchShiftHourSaga);

  // DLS Configuration sagas
  yield takeEvery(
    types.FETCH_DLS_CONFIGURATION_REQUEST,
    fetchDLSConfigurationSaga,
  );
  yield takeEvery(
    types.SAVE_DLS_CONFIGURATION_REQUEST,
    saveDLSConfigurationSaga,
  );

  // Block Planning watchers
  yield takeEvery(types.FETCH_BLOCK_PLANNING_REQUEST, fetchBlockPlanningSaga);
  yield takeEvery(
    types.DELETE_BLOCK_PLANNING_REQUEST,
    deleteBlockPlanningDetailSaga,
  );
  yield takeEvery(
    types.FETCH_BLOCK_PLANNING_BY_ID_REQUEST,
    fetchBlockPlanningDetailByIdRequest,
  );
  yield takeEvery(types.SAVE_BLOCK_PLANNING_REQUEST, saveBlockPlanningSaga);
  // Day of Week Configuration sagas
  yield takeEvery(
    types.FETCH_DAY_OF_WEEK_CONFIGURATION_REQUEST,
    fetchDayOfWeekConfigurationSaga,
  );

  // Task Configuration
  yield takeEvery(
    types.FETCH_TASK_CONFIGURATION_DETAILS_REQUEST,
    fetchTaskConfigurationDetailsSaga,
  );
  yield takeEvery(
    types.SAVE_TASK_CONFIGURATION_REQUEST,
    saveTaskConfigurationSaga,
  );
  yield takeEvery(
    types.DELETE_TASK_CONFIGURATION_REQUEST,
    deleteTaskConfigurationSaga,
  );
  yield takeEvery(
    types.FETCH_TASK_CONFIGURATION_BY_ID_REQUEST,
    fetchTaskConfigurationByIdSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_TASK_CONFIGURATION_REQUEST,
    activateDeactivateTaskConfigurationSaga,
  );
  yield takeEvery(types.TASK_BULK_UPDATE_REQUEST, saveTaskBulkUpdateSaga);
  yield takeEvery(types.FETCH_TASK_TAGS_REQUEST, fetchTaskTagsSaga);
  yield takeEvery(types.SAVE_TASK_TAGS_REQUEST, saveTaskTagsSaga);

  yield takeEvery(
    types.FETCH_PROVIDER_TASK_POOL_REQUEST,
    fetchProviderTaskPoolSaga,
  );
  yield takeEvery(
    types.SAVE_PROVIDER_TASK_POOL_REQUEST,
    saveProviderTaskPoolSaga,
  );
  yield takeEvery(
    types.FETCH_PROVIDER_TASK_POOL_PROVIDER_OPTIONS_REQUEST,
    fetchProviderTaskPoolProviderOptionsSaga,
  );
  yield takeEvery(
    types.ADD_PROVIDER_TASK_POOL_PROVIDERS_REQUEST,
    addProviderTaskPoolProvidersSaga,
  );
  yield takeEvery(
    types.REMOVE_PROVIDER_TASK_POOL_PROVIDER_REQUEST,
    removeProviderTaskPoolProviderSaga,
  );
  yield takeEvery(
    types.EXPORT_PROVIDER_TASK_POOL_REQUEST,
    exportProviderTaskPoolSaga,
  );

  // Simulation Cases watchers
  yield takeEvery(types.FETCH_SIMULATION_REQUEST, fetchSimulationDetailsSaga);
  yield takeEvery(types.SAVE_SIMULATION_REQUEST, saveSimulationDetailsSaga);
  yield takeEvery(types.DELETE_SIMULATION_REQUEST, deleteSimulationDetailSaga);
  yield takeEvery(
    types.FETCH_SIMULATION_BY_ID_REQUEST,
    fetchSimulationDetailByIdSaga,
  );
}

export default configurationSaga;
